/* eslint-disable no-magic-numbers,max-statements */
import { i18n } from "@/i18n";
import moment from "moment-timezone";
import { meetingAPI, calendarAPI } from "@/api";
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import {
	TeamMeetingAssignmentEvent,
	TeamMeetingUnassignmentEvent,
	MeetingTopicAddedEvent,
	MeetingTopicEditedEvent,
	MeetingTopicDeletedEvent,
	OneOnOneMeetingAssignmentEvent,
	ManualCalendarSyncEvent
} from "@/telemetry";
import { MEETING_SERIES_PAGE_SIZE } from "./constants";
import debugWrapper from "@/helpers/debugWrapper";
import MeetingDisplayState from "@/constants/MeetingDisplayState";
import TeamType from "@/constants/TeamType";

export default debugWrapper( "teaming:store:schedule:actions", {
	syncSchedule: async ( { commit, } ) => {
		commit( "toggleIsScheduleSyncing", true );
		commit( "toggleHasScheduleSyncFailed", false );
		try {
			await calendarAPI.resyncFullCalendar();

			const telemetryEvent = new ManualCalendarSyncEvent();
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_reSyncError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
			commit( "toggleHasScheduleSyncFailed", true );
			commit( "toggleIsScheduleSyncing", false );
		}
		// do NOT commit toggleIsScheduleSyncing here
	},
	refreshSchedule: async ( { commit, state, rootGetters, dispatch, }, { offset = 0, forceRefresh = false, } ) => {
		Nprogress.start();
		commit( "toggleRefreshingSchedule", true );
		try {
			const force = forceRefresh || state.invalidatedOffsets.includes( offset );
			if ( force || !( offset in state.offsetMeetingMap ) ) {
				const payload = await meetingAPI.getMeetingsByWeekOffset( { offset, } );
				const timezone = rootGetters[ "auth/timezone" ];
				const userProfile = rootGetters[ "userProfile/userProfile" ];
				const meetings = payload.value;
				commit( "scheduleRefreshed", { meetings, offset, userProfile, timezone, } );
				commit( "weekOffsetUpdated", { offset, } );
				dispatch( "meeting/processScheduleRefresh", { meetings, }, { root: true, } );
			} else {
				commit( "weekOffsetUpdated", { offset, } );
			}
			commit( "toggleHasScheduleSyncFailed", false );
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_refreshScheduleError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		commit( "toggleRefreshingSchedule", false );
		commit( "toggleIsBackgroundRefreshWaiting", false );
		commit( "toggleIsScheduleSyncing", false );
		Nprogress.done();
	},
	refreshPermissions: async ( { commit, rootGetters, } ) => {
		const timezone = rootGetters[ "auth/timezone" ];
		const userProfile = rootGetters[ "userProfile/userProfile" ];
		commit( "userProfileUpdated", { userProfile, timezone, } );
	},
	updateInBackground: async ( { commit, dispatch, state, } ) => {
		commit( "invalidateOffsetMeetingMap" );
		if ( state.isRefreshAllowed ) {
			commit( "toggleIsBackgroundUpdate", true );
			await dispatch( "refreshSchedule", {
				offset: state.selectedWeekOffset,
			} );
			commit( "toggleIsBackgroundUpdate", false );
		} else {
			commit( "toggleIsBackgroundRefreshWaiting", true );
		}
	},
	selectScheduleFilterOption: async ( { commit, }, filterOption ) => {
		commit( "setFilterOption", filterOption );
	},
	hideMeetingInTeaming: async ( { commit, rootGetters, }, { meetingId, meetingState, } ) => {
		await meetingAPI.updateMeeting( { meetingId, meetingState, displayState: MeetingDisplayState.HIDDEN, } );
		const meeting = await meetingAPI.getMeeting( { meetingId, }, { version: 2, } );
		const timezone = rootGetters[ "auth/timezone" ];
		const userProfile = rootGetters[ "userProfile/userProfile" ];
		commit( "meetingUpdated", { meeting, userProfile, timezone, } );
	},
	showMeetingInTeaming: async ( { commit, rootGetters, }, { meetingId, meetingState, } ) => {
		await meetingAPI.updateMeeting( { meetingId, meetingState, displayState: MeetingDisplayState.ACTIVE, } );
		const meeting = await meetingAPI.getMeeting( { meetingId, }, { version: 2, } );
		const timezone = rootGetters[ "auth/timezone" ];
		const userProfile = rootGetters[ "userProfile/userProfile" ];
		commit( "meetingUpdated", { meeting, userProfile, timezone, } );
	},

	/** TODO: topics are a meeting agenda store concern - move */
	createMeetingTopic: async ( { commit, rootGetters, }, { meetingId, topic, } ) => {
		commit( "toggleIsSavingTopics", true );
		Nprogress.start();
		try {
			const createdTopic = await meetingAPI.createTopic( {
				meetingId,
				topic,
			} );

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "clearNewTopic" );

			commit( "meetingTopicCreated", {
				meetingId,
				createdTopic,
				userProfile,
				timezone,
			} );

			const telemetryEvent = new MeetingTopicAddedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_createMeetingTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsSavingTopics", false );
	},
	/** TODO: topics are a meeting agenda store concern - move */
	updateMeetingTopic: async ( { commit, /* state, */ rootGetters, }, { topic, } ) => {
		commit( "toggleIsSavingTopics", true );
		Nprogress.start();
		try {
			const { meetingId, } = topic;
			const options = { version: 2, };

			await meetingAPI.updateTopic( {
				meetingId,
				topic,
			} );

			// bandaid fix for now: retrieving all topics again
			const mtg = await meetingAPI.getMeeting( { meetingId, }, options );
			const updatedTopics = mtg.topics;

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "meetingTopicsUpdated", {
				meetingId,
				updatedTopics,
				userProfile,
				timezone,
			} );

			const telemetryEvent = new MeetingTopicEditedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_updateMeetingTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsSavingTopics", false );
	},
	/** TODO: topics are a meeting agenda store concern - move */
	reSortTopics: async ( { commit, rootGetters, }, { meetingId, resortedTopicIds, } ) => {
		commit( "toggleIsResortingTopics", true );
		Nprogress.start();
		try {
			const updatedTopics = await meetingAPI.sortTopics( {
				meetingId,
				topicIds: resortedTopicIds,
			} );

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "meetingTopicsUpdated", {
				meetingId,
				updatedTopics,
				userProfile,
				timezone,
			} );
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_reSortTopicsError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsResortingTopics", false );
	},
	/** TODO: topics are a meeting agenda store concern - move */
	requestTopics: async ( { commit, state, rootGetters, }, { meetingId, } ) => {
		const hasMadeRequest = state.meetingTopicRequestMap[ meetingId ];
		if ( hasMadeRequest ) {
			return;
		}

		commit( "toggleIsRequestingTopics", true );
		Nprogress.start();
		try {
			await meetingAPI.sendTopicRequest( { meetingId, } );

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "topicsRequested", { meetingId, timezone, userProfile, } );
		} catch ( e ) {
			VueNotifications.error( {
				message: e.message || e || "Unexpected error",
			} );
			console.error( e );
		}
		Nprogress.done();
		commit( "toggleIsRequestingTopics", false );
	},
	/** TODO: topics are a meeting agenda store concern - move */
	deferTopic: async ( { commit, rootGetters, }, { meetingId, topicId, } ) => {
		commit( "toggleIsSavingTopics", true );
		Nprogress.start();
		try {
			await meetingAPI.deferTopic( {
				meetingId,
				topicId,
			} );

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "topicDeferred", {
				meetingId,
				topicId,
				userProfile,
				timezone,
			} );
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_deferTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsSavingTopics", false );
	},
	/** TODO: topics are a meeting agenda store concern - move */
	undeferTopic: async ( { commit, rootGetters, }, { meetingId, topicId, } ) => {
		commit( "toggleIsSavingTopics", true );
		Nprogress.start();
		try {
			await meetingAPI.undeferTopic( {
				meetingId,
				topicId,
			} );

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "topicUndeferred", {
				meetingId,
				topicId,
				userProfile,
				timezone,
			} );
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_undeferTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsSavingTopics", false );
	},
	/** TODO: topics are a meeting agenda store concern - move */
	pinTopic: async ( { commit, rootGetters, }, { meetingId, topicId, } ) => {
		commit( "toggleIsSavingTopics", true );
		Nprogress.start();
		try {
			const { instanceTopic, } = await meetingAPI.pinTopic( {
				meetingId,
				topicId,
			} );

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "topicPinned", {
				meetingId,
				topic: instanceTopic,
				userProfile,
				timezone,
			} );
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_pinTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsSavingTopics", false );
	},
	/** TODO: topics are a meeting agenda store concern - move */
	unpinFutureTopics: async ( { commit, rootGetters, }, { meetingId, topicId, } ) => {
		commit( "toggleIsSavingTopics", true );
		Nprogress.start();
		try {
			const instanceTopic = await meetingAPI.unpinFutureTopics( {
				meetingId,
				topicId,
			} );

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "topicUnpinned", {
				meetingId,
				topic: instanceTopic,
				userProfile,
				timezone,
			} );
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_unpinTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsSavingTopics", false );
	},
	/** TODO: topics are a meeting agenda store concern - move */
	removeTopic: async ( { commit, /* state, */ rootState, /* rootGetters, */ }, { meetingId, topicId, } ) => {
		// NOTE: cannot remove a topic if a meeting is in progress
		commit( "toggleIsRemovingTopics", true );
		Nprogress.start();

		try {
			const outcomeCount = ( rootState.meetingAgenda.topicOutcomeIdMap[ topicId ] || [] ).length;
			if ( outcomeCount > 0 ) {
				VueNotifications.error( {
					message: i18n.t( "scheduleStore.store_tooManyOutcomesError" ),
				} );
				return;
			}

			// first, actually remove the topic
			await meetingAPI.removeTopic( {
				meetingId,
				topicId,
			} );

			const telemetryEvent = new MeetingTopicDeletedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_removeTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleIsRemovingTopics", false );
		}
	},
	/** TODO: topics are a meeting agenda store concern - move */
	removeThisPinnedTopic: async ( { commit, /* state, */ rootState, /* rootGetters */ }, { meetingId, topicId, } ) => {
		commit( "toggleIsRemovingTopics", true );
		Nprogress.start();

		try {
			const outcomeCount = ( rootState.meetingAgenda.topicOutcomeIdMap[ topicId ] || [] ).length;
			if ( outcomeCount > 0 ) {
				VueNotifications.error( {
					message: i18n.t( "scheduleStore.store_tooManyOutcomesError" ),
				} );
				return;
			}

			await meetingAPI.unpinOnlyThisTopic( {
				meetingId,
				topicId,
			} );

			commit( "topicInstanceUnpinned", {
				meetingId,
				topicId,
			} );

			const telemetryEvent = new MeetingTopicDeletedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_removeTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleIsRemovingTopics", false );
		}
	},
	/** TODO: topics are a meeting agenda store concern - move */
	// NOTE: this is largely identical to removeThisPinnedTopic - could consider combining
	removeThisAndFuturePinnedTopics: async ( { commit, /* state, */ rootState, /* rootGetters */ }, { meetingId, topicId, } ) => {
		commit( "toggleIsRemovingTopics", true );
		Nprogress.start();

		try {
			const outcomeCount = ( rootState.meetingAgenda.topicOutcomeIdMap[ topicId ] || [] ).length;
			if ( outcomeCount > 0 ) {
				VueNotifications.error( {
					message: i18n.t( "scheduleStore.store_tooManyOutcomesError" ),
				} );
				return;
			}

			await meetingAPI.unpinThisAndFutureTopics( {
				meetingId,
				topicId,
			} );

			commit( "topicInstanceUnpinned", {
				meetingId,
				topicId,
			} );

			const telemetryEvent = new MeetingTopicDeletedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_removeTopicError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleIsRemovingTopics", false );
		}
	},
	/** TODO: outcomes are a meeting agenda store concern - move */
	fetchMeetingOutcomes: async ( { commit, rootGetters, /* state, rootState, */ }, { meetingId, } ) => {
		commit( "toggleIsFetchingMeetingCommitments", true );
		Nprogress.start();
		try {
			const outcomes = await meetingAPI.fetchOutcomes( { meetingId, } );
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			commit( "outcomesFetched", { meetingId, outcomes, userProfile, timezone, } );
		} catch ( e ) {
			const message = i18n.t( "", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsFetchingMeetingCommitments", false );
	},
	// This is a "coordinating action" that handles coordinating actions and mutations across multiple stores
	loadTeamGoals: async ( { commit, dispatch, /* state, rootState, rootGetters */ }, { meetingId, teamId, } ) => {
		commit( "toggleIsFetchingTeamGoals", true );
		commit( "goals/clearGoals", {}, { root: true, } );
		commit( "goals/setGoalFilterCriteria", { teamId, meetingId, }, { root: true, } );
		await dispatch( "goals/fetchGoalsAndMetadata", { forceRefresh: true, }, { root: true, } );
		commit( "toggleIsFetchingTeamGoals", false );
	},
	// This is a "coordinating action" that handles coordinating actions and mutations across multiple stores
	assignTeamToMeetingSeries: async (
		{ commit, dispatch, /* rootState, rootGetters, */ },
		{ meetingId, teamId, assignmentLocation = "", }
	) => {
		commit( "toggleIsFetchingTeamGoals", true );
		commit( "goals/clearGoals", {}, { root: true, } );

		await dispatch( "createTeamMeetingAssociation", { meetingId, teamId, assignmentLocation, } );
		commit( "goals/setGoalFilterCriteria", { teamId, meetingId, }, { root: true, } );

		await dispatch( "goals/fetchGoalsAndMetadata", {}, { root: true, } );

		commit( "toggleIsFetchingTeamGoals", false );

		await dispatch( "updateInBackground" );
	},
	createTeamMeetingAssociation: async ( { commit, /* dispatch, */ rootState, rootGetters, }, { meetingId, teamId, assignmentLocation = "", } ) => {
		commit( "toggleIsAssigningTeam", true );
		Nprogress.start();
		try {
			// NOTE: this returns the meeting, with the correct meeting schema
			const updatedMeeting = await meetingAPI.createTeamMeetingAssociation( {
				meetingId,
				teamId,
			} );

			const meetingTeam = rootState.apiTeams.myTeams.find( myTeam => myTeam.id === teamId ) || {};
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			commit( "meetingUpdated", { meeting: updatedMeeting, userProfile, timezone, } );

			if ( meetingTeam.type === TeamType.TEAM ) {
				const event = new TeamMeetingAssignmentEvent( meetingTeam.name, assignmentLocation );
				event.submit();
			} else if ( meetingTeam.type === TeamType.ONE_ON_ONE ) {
				const event = new OneOnOneMeetingAssignmentEvent( meetingTeam.name, assignmentLocation );
				event.submit();
			} else {
				console.warn( `no team assignment telemetry event defined for team of type ${meetingTeam.type}` );
			}
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_assignTeamToMeetingError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsAssigningTeam", false );
	},
	// This is a "coordinating action" that handles coordinating actions and mutations across multiple stores
	unassignTeamFromMeetingSeries: async ( { commit, dispatch, /* rootState, rootGetters, */ }, { meetingId, teamId, } ) => {
		commit( "toggleIsFetchingTeamGoals", true );
		commit( "goals/clearGoals", {}, { root: true, } );
		await dispatch( "deleteTeamMeetingAssociation", { meetingId, teamId, } );
		commit( "toggleIsFetchingTeamGoals", false );
		await dispatch( "updateInBackground" );
	},
	deleteTeamMeetingAssociation: async ( { commit, rootState, rootGetters, }, { meetingId, teamId, } ) => {
		commit( "toggleIsAssigningTeam", true );
		Nprogress.start();
		try {
			const meetingTeam = rootState.apiTeams.myTeams.find( myTeam => myTeam.id === teamId ) || {};
			const event = new TeamMeetingUnassignmentEvent( meetingTeam.name );

			// NOTE: this returns the meeting, with the correct meeting schema
			const updatedMeeting = await meetingAPI.deleteTeamMeetingAssociation( {
				meetingId,
				teamId,
			} );

			event.submit();
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			commit( "meetingUpdated", { meeting: updatedMeeting, userProfile, timezone, } );
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_unassignTeamFromMeetingError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsAssigningTeam", false );
	},
	/** TODO: commitments are a meeting agenda store concern - move */
	changeCommitmentCompletion: async ( { commit, rootGetters, /* rootState, */ }, { meetingId, commitmentId, isCompleted, } ) => {
		commit( "toggleIsChangingCommitmentCompletion", true );
		Nprogress.start();
		try {
			const timezone = rootGetters[ "auth/timezone" ];
			const commitments = rootGetters[ "schedule/meetingOutcomes" ]( meetingId ) || [];
			const meetingCommitment = commitments.find( commitment => commitment.id === commitmentId );
			if ( !meetingCommitment ) {
				throw new Error( i18n.t( "scheduleStore.store_missingCommitmentError" ) );
			}

			await meetingAPI.saveOutcomeCompletion( {
				outcomeId: commitmentId,
				isCompleted,
			} );

			const now = moment.tz( new Date(), timezone ).unix();
			const updatedCommitment = {
				...meetingCommitment,
				completed: isCompleted ? now : null,
			};

			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "outcomeUpdated", {
				meetingId,
				updatedOutcome: updatedCommitment,
				userProfile,
				timezone,
			} );
		} catch ( e ) {
			const message = i18n.t( "scheduleStore.store_updateCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsChangingCommitmentCompletion", false );
	},
	fetchMeetingSeries: async ( { commit, state, rootGetters, /* rootState, */ }, { meetingSeriesId, plannedStart, resetPaging = false, } ) => {
		commit( "toggleIsFetchingMeetingSeries", true );
		Nprogress.start();
		try {
			if ( resetPaging ) {
				commit( "clearMeetingSeries", { meetingSeriesId, } );
			}

			const timezone = rootGetters[ "auth/timezone" ];
			const offset = state.meetingSeriesOffsetMap[ meetingSeriesId ];
			const plannedStartFrom = moment.tz( plannedStart * 1000, timezone ).format( "YYYY-MM-DD" );

			const response = await meetingAPI.fetchMeetingSeries( {
				meetingSeriesId,
				plannedStartFrom,
				offset,
				limit: MEETING_SERIES_PAGE_SIZE,
			} );

			const userProfile = rootGetters[ "userProfile/userProfile" ];

			commit( "meetingSeriesFetched", {
				meetingSeriesId,
				seriesMeetings: response.value,
				userProfile,
				timezone,
			} );
		} catch ( e ) {
			const message = i18n.t( "", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsFetchingMeetingSeries", false );
	},
	selectScheduleMeeting: async ( { commit, dispatch, getters, rootGetters, }, { meetingId, } ) => {
		commit( "selectedMeetingChanged", meetingId );
		let meeting = getters.selectedMeeting;
		const selectedScheduleDate = getters.selectedScheduleDate;
		if ( !meeting ) {
			meeting = await meetingAPI.getMeeting( { meetingId, }, { version: "2", } );
		}
		if ( meeting?.plannedStart ) {
			const timezone = rootGetters[ "auth/timezone" ];
			const scheduleDate = moment.unix( meeting.plannedStart ).tz( timezone );
			const rightNow = moment().utc().tz( timezone );
			const offset = scheduleDate.clone().endOf( "week" ).diff(
				rightNow.clone().endOf( "week" ), "week"
			);
			if ( !selectedScheduleDate.isSame( scheduleDate, "day" ) ) {
				commit( "selectScheduleDate", scheduleDate );
				await dispatch( "refreshSchedule", { offset, } );
			}
		}
	},
}, { skipFirstArg: true, } );



// WEBPACK FOOTER //
// ./src/store/modules/schedule/actions.js